import React, { useEffect, useState } from "react";
import { CheckCircle2, X, ArrowRight } from "lucide-react";

/**
 * RolePainReliefStrip
 * --------------------------------------------------
 * Restrained two-column "what disappears for you" strip pinned at the
 * top of a role hub. Left column = the operational pain the role
 * currently absorbs daily (struck-through). Right column = how the
 * platform absorbs that pain instead.
 *
 * Restrained. No marketing copy. No hype. Each line is a real
 * operational reality.
 */

const PRESETS = {
  pm: {
    eyebrow: "PM · Operational relief",
    headline: "What disappears the day you turn this on.",
    pains: [
      "Buried in calls, texts, and emails chasing reports",
      "Hunting daily reports across 4 different inboxes",
      "Reactive firefighting instead of proactive scheduling",
      "Owner asks for status — you spend an hour pulling it together",
    ],
    reliefs: [
      "Every submission auto-routes to your console with crew + job stamped",
      "Daily reports land as branded PDFs with attendance + production",
      "Real-time field visibility · escalation chains visible at glance",
      "Owner-ready exec dashboard · same data · always live",
    ],
  },
  super: {
    eyebrow: "Superintendent · Operational relief",
    headline: "Less paperwork. More production.",
    pains: [
      "End-of-day filing that eats your evening",
      "Crew certs you can't verify in the field",
      "Pre-Op fails buried in someone's clipboard",
      "Weather call and nobody hears about it for 30 minutes",
    ],
    reliefs: [
      "QR sign-in · attendance auto-stamped per op",
      "Cert + acks visible on the operator profile in seconds",
      "Pre-Op fail = unit lockout + shop notified · automatic",
      "Weather hold pushes to crews + PM + payroll instantly",
    ],
  },
  shop: {
    eyebrow: "Shop · Operational relief",
    headline: "Equipment kept operational. Downtime visible.",
    pains: [
      "Surprise breakdowns nobody flagged",
      "Repair status updates over text and voicemail",
      "RTS approvals that never make it back to dispatch",
      "Job cost tied to a unit you forgot to log downtime on",
    ],
    reliefs: [
      "Pre-Op fails route here in real time · unit auto-locked",
      "Repair queue with mechanic ETA visible to PM + dispatch",
      "RTS signature + photos · auto-tags the job",
      "Downtime + cost captured per unit · per job · automatic",
    ],
  },
  admin: {
    eyebrow: "Owner · Operational relief",
    headline: "Your company. One screen.",
    pains: [
      "Status meetings to find out what already happened",
      "Compliance trails scattered across binders + drives",
      "Risk you only see after it's a problem",
      "Three systems · none of them talking to each other",
    ],
    reliefs: [
      "Project health table · every active job · one row each",
      "OSHA-ready audit trail rolled up automatically",
      "Risk heat-map · escalation chains visible same-day",
      "One platform · routing + records + KPIs + workforce",
    ],
  },
};

export default function RolePainReliefStrip({ role = "pm" }) {
  const cfg = PRESETS[role] || PRESETS.pm;

  // Quiet operational receipt — when a real demo submission fires
  // anywhere in the app, we increment a session-scoped count and append
  // a subtle " · {n} just routed in this session" tail to the FIRST
  // relief bullet. No animation, no glow — enterprise telemetry.
  const [sessionCount, setSessionCount] = useState(0);
  useEffect(() => {
    const onSubmit = () => setSessionCount((c) => c + 1);
    window.addEventListener("forgeops:submission", onSubmit);
    return () => window.removeEventListener("forgeops:submission", onSubmit);
  }, []);

  return (
    <section
      className="rounded-md overflow-hidden"
      style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
      data-testid={`role-pain-relief-${role}`}
    >
      <div
        className="px-5 sm:px-7 py-5 border-b"
        style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}
      >
        <div
          className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold mb-2"
          style={{ color: "#FF6A00" }}
        >
          {cfg.eyebrow}
        </div>
        <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white">
          {cfg.headline}
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px"
           style={{ background: "#1A1A1A" }}>
        <div
          className="p-5 sm:p-6"
          style={{ background: "#0F0F0F" }}
          data-testid="role-pain-column"
        >
          <div
            className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold mb-3 inline-flex items-center gap-2"
            style={{ color: "#FF6A6A" }}
          >
            <X className="w-3 h-3" />
            Today · without the platform
          </div>
          <ul className="space-y-2.5">
            {cfg.pains.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[13px] leading-relaxed"
                style={{ color: "#888" }}
              >
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "#FF6A6A" }}
                />
                <span style={{ textDecoration: "line-through", textDecorationColor: "rgba(255,106,106,0.5)" }}>
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div
          className="p-5 sm:p-6"
          style={{ background: "#0F0F0F" }}
          data-testid="role-relief-column"
        >
          <div
            className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold mb-3 inline-flex items-center gap-2"
            style={{ color: "#65DA76" }}
          >
            <CheckCircle2 className="w-3 h-3" />
            With ForgedOps · routed automatically
          </div>
          <ul className="space-y-2.5">
            {cfg.reliefs.map((r, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[13px] leading-relaxed"
                style={{ color: "#FFFFFF" }}
              >
                <ArrowRight
                  className="w-3.5 h-3.5 mt-0.5 shrink-0"
                  style={{ color: "#FF6A00" }}
                />
                <span>
                  {r}
                  {i === 0 && sessionCount > 0 && (
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.2em] ml-2"
                      style={{ color: "#FF6A00" }}
                      data-testid="role-relief-session-receipt"
                    >
                      · {sessionCount} just routed in this session
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
